import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { healthCyclesAPI } from '../../services/api'
import { errorCode, formatDate } from './cycleStatus'
import MarkerHistoryRows from './MarkerHistoryRows'
import { markersWithHistory } from './supplementaryFormat'

// VH-233: the cycle's supplementary tests — list in draw-date order, add from the tests in the
// cycle window, detach with confirmation. Attach/detach rerun the cycle and are locked while it runs.
export function SupplementaryTestsSection({ cycleId, active = false, payload = null }) {
  const { t, i18n } = useTranslation()
  const queryClient = useQueryClient()
  const [picking, setPicking] = useState(false)
  const [picked, setPicked] = useState('')
  const [confirmDetach, setConfirmDetach] = useState(null)
  const [error, setError] = useState(null)
  const queryKey = ['healthCycleSupplementary', cycleId]

  const { data, isLoading } = useQuery({
    queryKey,
    queryFn: () => healthCyclesAPI.listSupplementaryTests(cycleId),
    select: (response) => response.data,
    enabled: !!cycleId,
  })

  const done = () => {
    setError(null)
    setPicking(false)
    setPicked('')
    setConfirmDetach(null)
    queryClient.invalidateQueries({ queryKey })
    queryClient.invalidateQueries({ queryKey: ['healthCycle', cycleId] })
    queryClient.invalidateQueries({ queryKey: ['healthCycleVersion', cycleId] })
    queryClient.invalidateQueries({ queryKey: ['healthCycleTestChecklist', cycleId] })
  }
  const fail = (err) => setError(t(`healthCycle.errors.${errorCode(err)}`, t('healthCycle.errors.generic')))
  const attach = useMutation({ mutationFn: (id) => healthCyclesAPI.attachSupplementaryTest(cycleId, id), onSuccess: done, onError: fail })
  const detach = useMutation({ mutationFn: (id) => healthCyclesAPI.detachSupplementaryTest(cycleId, id), onSuccess: done, onError: fail })

  const locked = active || attach.isPending || detach.isPending
  const attached = data?.attached ?? []
  const eligible = data?.eligible ?? []
  const pickedTest = eligible.find((e) => String(e.blood_test_id) === picked)
  const history = markersWithHistory(payload)

  return (
    <section className="card" aria-label={t('healthCycle.supplementary.title')} data-testid="supplementary-tests-section">
      <div className="card-body space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <h3 className="text-sm font-semibold text-gray-900">{t('healthCycle.supplementary.title')}</h3>
            <p className="text-xs text-gray-500">{t('healthCycle.supplementary.intro')}</p>
          </div>
          {!picking && (
            <button type="button" className="btn-outline text-sm" disabled={locked} onClick={() => setPicking(true)}>
              {t('healthCycle.supplementary.add')}
            </button>
          )}
        </div>

        {active && <p className="text-xs text-primary-700">{t('healthCycle.supplementary.running')}</p>}
        {error && <p className="text-sm text-red-600" role="alert">{error}</p>}

        {picking && (
          <div className="rounded-md border border-gray-200 p-3 space-y-2">
            {eligible.length === 0 ? (
              <p className="text-sm text-gray-600">{t('healthCycle.supplementary.noEligible')}</p>
            ) : (
              <label className="block text-sm text-gray-700">
                {t('healthCycle.supplementary.pick')}
                <select className="mt-1 block w-full text-sm" value={picked} onChange={(e) => setPicked(e.target.value)}>
                  <option value="">—</option>
                  {eligible.map((e) => (
                    <option key={e.blood_test_id} value={e.blood_test_id}>
                      {formatDate(e.test_date, i18n.language)}{e.lab_name ? ` — ${e.lab_name}` : ''} · {t('healthCycle.supplementary.markers', { count: e.marker_count })}
                      {' · '}{e.partial ? t('healthCycle.supplementary.partial') : t('healthCycle.supplementary.full')}
                    </option>
                  ))}
                </select>
              </label>
            )}
            {pickedTest && !pickedTest.attachable && (
              <p className="text-sm text-amber-700" role="alert">{t(`healthCycle.supplementary.reason.${pickedTest.reason}`, pickedTest.reason)}</p>
            )}
            <div className="flex gap-2">
              <button type="button" className="btn-outline text-xs" onClick={() => { setPicking(false); setPicked('') }}>
                {t('healthCycle.supplementary.cancel')}
              </button>
              <button type="button" className="btn-primary text-xs" disabled={locked || !pickedTest?.attachable}
                onClick={() => attach.mutate(pickedTest.blood_test_id)}>
                {t('healthCycle.supplementary.attach')}
              </button>
            </div>
          </div>
        )}

        {isLoading ? (
          <p className="text-sm text-gray-500">{t('common.loading')}</p>
        ) : attached.length === 0 ? (
          <p className="text-sm text-gray-500" data-testid="supplementary-empty">{t('healthCycle.supplementary.empty')}</p>
        ) : (
          <ul className="space-y-2">
            {attached.map((test) => (
              <li key={test.blood_test_id} className="rounded-md border border-gray-200 p-3 text-sm" data-testid={`supplementary-test-${test.blood_test_id}`}>
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="font-medium text-gray-900">
                      {formatDate(test.test_date, i18n.language)}{test.lab_name ? ` — ${test.lab_name}` : ''}
                    </p>
                    <p className="text-xs text-gray-600">
                      {t('healthCycle.supplementary.markers', { count: test.marker_count })}: {test.marker_names.join(', ')}
                    </p>
                  </div>
                  {confirmDetach !== test.blood_test_id && (
                    <button type="button" className="text-sm text-red-600 hover:text-red-800 disabled:opacity-50 disabled:cursor-not-allowed" disabled={locked}
                      onClick={() => setConfirmDetach(test.blood_test_id)}>
                      {t('healthCycle.supplementary.detach')}
                    </button>
                  )}
                </div>
                {confirmDetach === test.blood_test_id && (
                  <div className="mt-2 rounded-md border border-red-200 bg-red-50 p-3" role="alertdialog" data-testid="detach-confirm">
                    <p className="font-medium text-red-800">{t('healthCycle.supplementary.detachTitle', { date: formatDate(test.test_date, i18n.language) })}</p>
                    <p className="text-red-800">{t('healthCycle.supplementary.detachWarning')}</p>
                    <div className="mt-2 flex gap-2">
                      <button type="button" className="btn-outline text-xs" onClick={() => setConfirmDetach(null)}>{t('common.cancel')}</button>
                      <button type="button" className="btn-danger text-xs" disabled={locked} onClick={() => detach.mutate(test.blood_test_id)}>
                        {t('healthCycle.supplementary.detachConfirm')}
                      </button>
                    </div>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}

        <MarkerHistoryRows results={history} />
      </div>
    </section>
  )
}

export default SupplementaryTestsSection
